import type { ModeNames } from "@/modes/mode";
import type { CurrentScene } from "@/modes/mode-selectors";
import type { CurrentAreaData } from "@/modes/select-current-area-data";
import {
  selectComputeGaussianSplats,
  selectComputePointcloud,
} from "@/modes/walk-mode/walk-scene-selectors";
import { SceneFilter } from "@/types/scene-filter";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { selectIElementsSlice } from "../ielements-slice-selector";
import { curryAppSelector } from "../reselect";
import type { RootState } from "../store";

/** State specific for the flywalk mode */
export type FlyWalkModeState = {
  /** Type of filtering for main element in walk mode */
  flyWalkSceneFilter: SceneFilter;
};

type OnlyFlyWalkModeStore = {
  flyWalkMode: FlyWalkModeState;
};

const initialState: FlyWalkModeState = {
  flyWalkSceneFilter: SceneFilter.Pano,
};

const flyWalkModeSlice = createSlice({
  initialState,
  name: "flyWalkMode",
  reducers: {
    setFlyWalkSceneFilter(state, action: PayloadAction<SceneFilter>) {
      state.flyWalkSceneFilter = action.payload;
    },
  },
});

export const { setFlyWalkSceneFilter } = flyWalkModeSlice.actions;

export const flyWalkModeReducer = flyWalkModeSlice.reducer;

/**
 *
 * @param state The current state of the store
 * @returns The FlyWalkMode slice of the store
 */
export function selectFlyWalkModeSlice(
  state: OnlyFlyWalkModeStore,
): FlyWalkModeState {
  return state.flyWalkMode;
}

/** @returns the selected scene filter to display in flywalk mode */
export function selectFlyWalkSceneFilter({
  flyWalkMode,
}: OnlyFlyWalkModeStore): SceneFilter {
  return flyWalkMode.flyWalkSceneFilter;
}

type Store = Pick<RootState, "flyWalkMode" | "iElements">;

export const selectCanRenderSceneFilter = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectFlyWalkSceneFilter,
      (store: Store, mode: ModeNames) => mode,
      (state: Store, mode: ModeNames, scene: CurrentScene) => scene,
      (
        state: Store,
        mode: ModeNames,
        scene: CurrentScene,
        area: CurrentAreaData,
      ) => area,
    ],
    (iElements, sceneFilter, mode, scene, area) => {
      switch (sceneFilter) {
        case SceneFilter.PointCloud:
        case SceneFilter.Overlay:
          return !!selectComputePointcloud(scene, area)({ iElements });
        case SceneFilter.GaussianSplats:
          return !!selectComputeGaussianSplats(scene, area)({ iElements });
        case SceneFilter.Cad:
          return true;
        case SceneFilter.Pano:
          return mode === "walk";
        default:
          return true;
      }
    },
  ),
);
